import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DocumentsPanel } from "../components/DocumentsPanel";
import { AdminReviewPanel } from "../components/AdminReviewPanel";
import { FinesPanel } from "../components/FinesPanel";
import { AccidentsPanel } from "../components/AccidentsPanel";
import { ComplaintsPanel } from "../components/ComplaintsPanel";
import { BlogCMSPanel } from "../components/BlogCMSPanel";
import { FleetGalleryPanel } from "../components/admin/FleetGalleryPanel";
import { ReviewsManager } from "../components/admin/ReviewsManager";
import { PartnerLeadsPipeline } from "../components/admin/PartnerLeadsPipeline";
import { MailOutbox } from "../components/admin/MailOutbox";
import { RenewalBanner } from "../components/account/RenewalBanner";
import { AccountTabs, LEGACY_TAB_MAP } from "../components/account/AccountTabs";
import { BookingCard } from "../components/account/BookingCard";
import { ProfileForm } from "../components/account/ProfileForm";
import { ExtendDialog } from "../components/account/ExtendDialog";
import { CancelDialog } from "../components/account/CancelDialog";
import { OverviewPanel } from "../components/account/OverviewPanel";
import { ServiceRequestsPanel } from "../components/account/ServiceRequestsPanel";
import { FeedbackPanel } from "../components/account/FeedbackPanel";

const Spinner = () => (
  <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#009900]" /></div>
);

export default function Account() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab") || "overview";
    return LEGACY_TAB_MAP[t] || t;
  });
  const [docs, setDocs] = useState([]);
  const [bookings, setBookings] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [cancelRef, setCancelRef] = useState(null);
  const [extendBooking, setExtendBooking] = useState(null);
  const [extendValue, setExtendValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  const goTab = (k) => setTab(LEGACY_TAB_MAP[k] || k);

  const loadReminders = useCallback(() => {
    api.get("/reminders").then((r) => setReminders(r.data)).catch(() => setReminders([]));
  }, []);

  const loadBookings = useCallback(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  }, []);

  useEffect(() => {
    if (user === null) navigate("/login?next=/account");
    if (user) {
      loadBookings();
      loadReminders();
      api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
      setProfile({ name: user.name || "", phone: user.phone || "", license_no: user.license_no || "", nationality: user.nationality || "" });
    }
  }, [user, navigate, loadBookings, loadReminders]);

  if (!user)
    return (
      <div className="min-h-screen flex flex-col bg-[#fafcfa]">
        <Header />
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>
        <Footer />
      </div>
    );

  const oneTapExtend = async (ref) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${ref}/extend`, { additional_months: 1 });
      toast.success("Subscription extended by 1 month");
      loadBookings();
      loadReminders();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${cancelRef}/cancel`);
      toast.success("Booking cancelled");
      loadBookings();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      setCancelRef(null);
    }
  };

  const doExtend = async () => {
    setBusy(true);
    try {
      const body = extendBooking.mode === "monthly"
        ? { additional_months: Number(extendValue || 1) }
        : { new_return_datetime: extendValue };
      await api.post(`/bookings/${extendBooking.booking_ref}/extend`, body);
      toast.success("Booking extended");
      loadBookings();
      setExtendBooking(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openExtend = (b) => {
    setExtendBooking(b);
    setExtendValue(b.mode === "monthly" ? "1" : dayjs(b.return_datetime).add(1, "day").format("YYYY-MM-DDTHH:mm"));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/profile", profile);
      await checkAuth();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const renderBookingCard = (b) => (
    <BookingCard key={b.booking_ref} b={b} isOpen={expanded === b.booking_ref}
      onToggle={() => setExpanded(expanded === b.booking_ref ? null : b.booking_ref)}
      onExtend={openExtend} onCancel={setCancelRef} />
  );

  const agreementList = (bookings || []).filter((b) => ["active", "upcoming"].includes(b.display_status));

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 w-full flex-1" data-testid="account-page">
        <div className="promo-stripe mb-6">
          <h1 className="heading-upper text-2xl text-[#144733]">My account</h1>
          <p className="text-sm text-[#57685e]">{user.name} · {user.email}</p>
          <Link to="/modify-reservation" className="text-xs text-[#009900] underline mt-1 inline-block" data-testid="account-modify-link">Need to change or cancel a booking? See the rules & fees</Link>
        </div>

        <RenewalBanner reminders={reminders} dismissed={dismissed} busy={busy}
          onExtend={oneTapExtend} onDismiss={(ref) => setDismissed((d) => [...d, ref])} />

        <AccountTabs tab={tab} setTab={setTab} isAdmin={user.role === "admin"} />

        {tab === "overview" && (bookings !== null
          ? <OverviewPanel bookings={bookings} docs={docs} onGoTab={goTab} />
          : <Spinner />)}

        {tab === "history" && (
          <div className="space-y-4">
            {bookings === null && <Spinner />}
            {bookings !== null && bookings.length === 0 && (
              <div className="bg-white border border-[#e2ebe4] rounded-lg p-10 text-center" data-testid="no-bookings">
                <p className="text-sm text-[#57685e] mb-4">No bookings yet.</p>
                <Link to="/" className="btn-cta rounded px-6 py-3 text-xs inline-block">Book your first car</Link>
              </div>
            )}
            {bookings !== null && bookings.map(renderBookingCard)}
          </div>
        )}

        {tab === "agreement" && (bookings === null ? <Spinner /> : (
          <div className="space-y-4" data-testid="agreement-panel">
            {agreementList.length === 0 ? (
              <div className="bg-white border border-[#e2ebe4] rounded-lg p-10 text-center" data-testid="no-agreement">
                <p className="text-sm text-[#57685e] mb-4">No active or upcoming rental agreement right now.</p>
                <Link to="/" className="btn-cta rounded px-6 py-3 text-xs inline-block">Book your next car</Link>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#57685e]">Your active and upcoming rentals — expand a booking to check in, extend, or download the rental agreement and invoice.</p>
                {agreementList.map(renderBookingCard)}
              </>
            )}
          </div>
        ))}

        {tab === "vehicledocs" && <DocumentsPanel />}

        {tab === "services" && (bookings === null ? <Spinner /> : (
          <div className="space-y-8">
            <ServiceRequestsPanel bookings={bookings} />
            <AccidentsPanel />
          </div>
        ))}

        {tab === "billing" && (bookings === null ? <Spinner /> : (
          <div className="space-y-8" data-testid="billing-panel">
            <FinesPanel bookings={bookings} />
            <ComplaintsPanel bookings={bookings} user={user} />
          </div>
        ))}

        {tab === "feedback" && <FeedbackPanel bookings={bookings || []} />}
        {tab === "review" && user.role === "admin" && <AdminReviewPanel />}
        {tab === "blogcms" && user.role === "admin" && <BlogCMSPanel />}
        {tab === "fleetgallery" && user.role === "admin" && <FleetGalleryPanel />}
        {tab === "reviews" && user.role === "admin" && <ReviewsManager />}
        {tab === "partnerleads" && user.role === "admin" && <PartnerLeadsPipeline />}
        {tab === "outbox" && user.role === "admin" && <MailOutbox />}
        {tab === "profile" && profile && (
          <ProfileForm profile={profile} setProfile={setProfile} busy={busy} onSave={saveProfile} />
        )}
      </main>

      <CancelDialog cancelRef={cancelRef} busy={busy} onConfirm={doCancel} onClose={() => setCancelRef(null)} />
      <ExtendDialog booking={extendBooking} value={extendValue} setValue={setExtendValue}
        busy={busy} onConfirm={doExtend} onClose={() => setExtendBooking(null)} />
      <Footer />
    </div>
  );
}
